import { useCallback } from "react";
import { useCurrentRapidData } from "@rapid/store";
import useApi, { useAsyncBoundOperation } from "@rapid/hooks/useApi";
import { useProjectSelector } from "@rapid/contexts/ProjectSelectorContext";

/**
 * Prediction from source document annotations (COCO format)
 */
export interface SourcePrediction {
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
    class: string;
    class_id: number;
    detection_id: string;
    parent_id?: string;
}

/**
 * Parsed annotation data from source document
 */
export interface SourceAnnotationData {
    image: { width: number; height: number };
    predictions: SourcePrediction[];
}

/**
 * Annotation data stored in a source document under annotations[annotationGroup]
 */
export interface SourceAnnotationEntry {
    original?: {
        annotation?: string; // JSON string of SourceAnnotationData
        format?: string;
        source?: string;
    };
    converted?: string;
    // Additional annotation metadata
    [key: string]: any;
}

/**
 * Source document from backend/Firestore
 */
export interface SourceDocument {
    id: string;
    owner: string;
    batches?: string[];
    width?: number;
    height?: number;
    name: string;
    // Annotations are keyed by annotation group (e.g., "objects", "egg-rapidDataId123")
    annotations?: Record<string, SourceAnnotationEntry>;
    // Add other source fields as needed
    [key: string]: any;
}

export interface ImproveBatchData {
    sources: SourceDocument[];
    batchRef: string;
    datasetId: string;
    /** The annotation group key from the dataset - use this to access source.annotations[annotationGroup] */
    annotationGroup: string;
}

/**
 * Parse annotations from a source document using the dataset's annotation group
 *
 * @param source - The source document from Firestore
 * @param annotationGroup - The annotation group key from dataset.annotation (e.g., "objects", "egg-rapidDataId123")
 * @returns Parsed predictions or null if no annotations available
 */
export function parseSourceAnnotations(
    source: SourceDocument,
    annotationGroup: string
): SourceAnnotationData | null {
    try {
        // Access annotations using the dataset's annotation group key
        const annotationEntry = source.annotations?.[annotationGroup];
        if (!annotationEntry) {
            console.warn(
                `[parseSourceAnnotations] No annotation found for group "${annotationGroup}" in source ${source.id}`
            );
            return null;
        }

        // The annotation JSON is stored in:
        // - `.converted` when saved via /query/updateAnnotation endpoint (format: {key, boxes, width, height})
        // - `.original.annotation` for original uploaded annotations (format: {image, predictions})
        // Prefer .converted as it contains the latest edits
        const convertedJson = annotationEntry.converted;
        const originalJson = annotationEntry.original?.annotation;

        if (convertedJson) {
            // Parse the converted format and transform to SourceAnnotationData format
            const converted = JSON.parse(convertedJson) as {
                key: string;
                boxes: Array<{
                    id: string;
                    label: string;
                    x: number | string;
                    y: number | string;
                    width: number | string;
                    height: number | string;
                    confidence?: number | null;
                    type?: string;
                }>;
                width: number;
                height: number;
            };

            // Transform boxes to predictions format
            const predictions: SourcePrediction[] = converted.boxes.map((box, index) => ({
                x: typeof box.x === "string" ? parseFloat(box.x) : box.x,
                y: typeof box.y === "string" ? parseFloat(box.y) : box.y,
                width: typeof box.width === "string" ? parseFloat(box.width) : box.width,
                height: typeof box.height === "string" ? parseFloat(box.height) : box.height,
                confidence: box.confidence ?? 1.0,
                class: box.label,
                class_id: index,
                detection_id: box.id
            }));

            return { image: { width: converted.width, height: converted.height }, predictions };
        }

        if (originalJson) {
            // Parse original format directly
            const parsed = JSON.parse(originalJson) as SourceAnnotationData;
            return parsed;
        }

        console.warn(
            `[parseSourceAnnotations] No converted or original.annotation found for source ${source.id}`
        );
        return null;
    } catch (err) {
        console.error(
            `[parseSourceAnnotations] Failed to parse annotations for source ${source.id}:`,
            err
        );
        return null;
    }
}

/**
 * Shared utility to fetch sources from the "rapid-review" batch.
 * This is used by both useImproveBatchLoader and useReviewBatchCount to avoid duplication.
 *
 * Query flow:
 * 1. Get dataset by rapidDataId
 * 2. Get "rapid-review" batch from dataset
 * 3. List sources in the batch
 *
 * @param rapidDataId - The rapid data ID
 * @param api - The API instance
 * @returns Object with sources, dataset, datasetId, batchId, and batchRef, or null if any step fails
 */
export async function fetchRapidReviewBatchSources(
    rapidDataId: string,
    api: ReturnType<typeof useApi>
): Promise<{
    sources: SourceDocument[];
    dataset: Awaited<ReturnType<typeof api.datasets.getDatasetByRapidDataId>>;
    datasetId: string;
    batchId: string;
    batchRef: string;
} | null> {
    // Step 1: Get dataset by rapidDataId
    const dataset = await api.datasets.getDatasetByRapidDataId(rapidDataId);

    if (!dataset) {
        return null;
    }

    const datasetId = dataset.id;

    // Step 2: Get "rapid-review" batch from dataset
    const batch = await api.datasets.getBatchByName(datasetId, "rapid-review");

    if (!batch) {
        return null;
    }

    const batchId = batch.id;
    const batchRef = `${datasetId}/${batchId}`;

    // Step 3: List sources in the batch
    const sources = await api.datasets.listSourcesInBatch(datasetId, batchId);

    return { sources: sources as SourceDocument[], dataset, datasetId, batchId, batchRef };
}

/**
 * Hook to load improve batch images.
 *
 * Query flow:
 * 1. Get rapidDataId from current rapid data
 * 2. Get dataset by rapidDataId
 * 3. Get "rapid-review" batch from dataset
 * 4. List sources in the batch
 */
export function useImproveBatchLoader() {
    const currentRapidData = useCurrentRapidData();
    const projectSelector = useProjectSelector();
    const api = useApi();
    const rapidDataId = currentRapidData?.id;

    const fetchImproveBatch = useCallback(
        async (abortController: AbortController): Promise<ImproveBatchData | null> => {
            if (!rapidDataId) {
                console.warn("[useImproveBatchLoader] No rapidDataId available");
                return null;
            }

            const batchData = await fetchRapidReviewBatchSources(rapidDataId, api);

            if (!batchData) {
                console.warn(
                    `[useImproveBatchLoader] Failed to fetch batch for rapidDataId: ${rapidDataId}`
                );
                return null;
            }

            const { sources, dataset, datasetId, batchRef } = batchData;
            const annotationGroup = dataset.annotation;

            if (!annotationGroup) {
                console.warn(
                    `[useImproveBatchLoader] Dataset ${datasetId} has no annotation field`
                );
                return null;
            }

            console.log(`[useImproveBatchLoader] Dataset annotation group: ${annotationGroup}`);
            console.log(
                `[useImproveBatchLoader] Found ${sources.length} sources in batch ${batchRef}`
            );

            return { sources, batchRef, datasetId, annotationGroup };
        },
        [api, rapidDataId]
    );

    const {
        isLoading: loading,
        error,
        result: improveBatch,
        invoke: loadImproveBatch,
        reset
    } = useAsyncBoundOperation(fetchImproveBatch, { autoInvoke: !!rapidDataId });

    console.log("[useImproveBatchLoader] State:", {
        rapidDataId,
        loading,
        error: error?.message,
        improveBatchExists: !!improveBatch,
        sourcesCount: improveBatch?.sources?.length ?? 0
    });

    /**
     * Build the CDN URL for a source image
     */
    const getSourceImageUrl = useCallback(
        (source: SourceDocument): string => {
            const cdnHost = projectSelector.cdnHost();
            const url = `https://${cdnHost}/${source.owner}/${source.id}/original.jpg`;
            console.log("[useImproveBatchLoader] getSourceImageUrl:", url);
            return url;
        },
        [projectSelector]
    );

    return {
        loading,
        error,
        improveBatch,
        sources: improveBatch?.sources ?? [],
        annotationGroup: improveBatch?.annotationGroup ?? null,
        loadImproveBatch,
        reset,
        getSourceImageUrl
    };
}
